import React, { useEffect, useMemo, useRef, useState } from "react";
import ActionMenu from "../../Components/ActionMenu";
import ActionIconButton from "../../Components/ActionIconButton";
import ActionsCell from "../../Components/ActionsCell";
import { buildCsvText, exportToCsv } from "../../utils/exportCsv";
import { studentCsvColumns, studentCsvTemplateRows } from "../../utils/studentCsv";
import {
  AlertTriangle,
  ArrowUpCircle,
  Award,
  BookOpen,
  Calendar,
  CheckCircle,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Download,
  Edit3,
  Eye,
  FileDown,
  FileText,
  Filter,
  GraduationCap,
  Layers,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Square,
  Trash2,
  TrendingUp,
  Upload,
  User2,
  UserCheck,
  Users,
  Wand2,
  X,
} from "lucide-react";
import { Card, Modal, message, Spin } from "antd";
import { API_BASE_URL } from "../../config";

// Constants
const server_url = API_BASE_URL;
const colors = {
  orange: "#D67C2C",
  blue: "#2173B7",
  green: "#22c55e",
  purple: "#a855f7"
};

const GRADE_LIST = [
  "BV1", "BV2", "BV3", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"
];

// Helper function for conditional class names
function classNames(...args) {
  return args.filter(Boolean).join(" ");
}

export default function StudentView() {
  // State
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [errorModalVisible, setErrorModalVisible] = useState(false);

  // Bulk Selection
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Add Student Modal
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Enhanced Bulk CSV Modal
  const [csvModalOpen, setCSVModalOpen] = useState(false);
  const [csvPreview, setCSVPreview] = useState([]);
  const [csvFile, setCSVFile] = useState(null);
  const [bulkGrade, setBulkGrade] = useState("");
  const [bulkSection, setBulkSection] = useState("");
  const [availableClassesForBulk, setAvailableClassesForBulk] = useState([]);
  const [selectedClassesForBulk, setSelectedClassesForBulk] = useState([]);
  const [loadingBulkClasses, setLoadingBulkClasses] = useState(false);

  // CSV Validation State
  const [csvValidation, setCSVValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const csvValidationRequest = useRef(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [showSkippedModal, setShowSkippedModal] = useState(false);

  // Bulk Profile Update Modal State
  const [profileUploadModalOpen, setProfileUploadModalOpen] = useState(false);
  const [profileCsvFile, setProfileCsvFile] = useState(null);
  const [profileCsvPreview, setProfileCsvPreview] = useState([]);
  const [profileValidation, setProfileValidation] = useState(null);
  const [validatingProfile, setValidatingProfile] = useState(false);
  const [profileUploadResult, setProfileUploadResult] = useState(null);
  const [showProfileSkippedModal, setShowProfileSkippedModal] = useState(false);
  const [profileTemplateInfo, setProfileTemplateInfo] = useState(null);


  // Assign Classes Modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [assignSectionFilter, setAssignSectionFilter] = useState("");

  // Bulk Edit Modal
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const [bulkEditGrade, setBulkEditGrade] = useState("");

  // Promote Student Modal
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [promoteGrade, setPromoteGrade] = useState("");

  // Student Detail Modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editProfileMode, setEditProfileMode] = useState(false);
  const [editProfileData, setEditProfileData] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);

  // Delete Confirmation Modal
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    open: false,
    type: 'single',
    email: '',
    students: [],
    step: 1
  });

  // New Student State
  const [formStudent, setFormStudent] = useState({
    name: "",
    email: "",
    password: "",
    grade: "",
    section: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  // Notification - matching LessonPlanHistory.js
  const [notification, setNotification] = useState(null);

  // Filter state - matching LessonPlanHistory.js style
  const [filters, setFilters] = useState({
    status: "",
    grade: "",
    section: "",
    school: ""
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Every distinct section used across the school's classes (for the Section filter
  // dropdown, so it lists all sections, not just ones already assigned to a loaded student)
  const [schoolSections, setSchoolSections] = useState([]);

  // Auto-assign all students to their matching grade/section class in one call
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [autoAssignResult, setAutoAssignResult] = useState(null);
  const [autoAssignModalOpen, setAutoAssignModalOpen] = useState(false);

  // Get access token
  const accessToken = localStorage.getItem("accessToken");

  // Fetch students with retry mechanism
  useEffect(() => {
    const fetchWithRetry = async () => {
      try {
        await fetchStudents();
      } catch (error) {
        // If we have retries left and there was an error, try again after a delay
        if (retryCount < 3) {
          console.log(`Retrying fetch (${retryCount + 1}/3)...`);
          setTimeout(() => {
            setRetryCount(prevCount => prevCount + 1);
          }, 1500); // Retry after 1.5 seconds
        } else {
          // If we've exhausted retries, show the error modal
          setErrorModalVisible(true);
        }
      }
    };

    fetchWithRetry();
    // eslint-disable-next-line
  }, [retryCount]);

  // Fetch every distinct section used across the school's classes, so the Section
  // filter can list all of them (A, B, C, ...) even if some don't have any students loaded yet.
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${server_url}/admin/classes/sections`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await resp.json();
        setSchoolSections(Array.isArray(data?.sections) ? data.sections : []);
      } catch (e) {
        setSchoolSections([]);
      }
    })();
    // eslint-disable-next-line
  }, []);

  // Update filtered students when students or search/filters change
  useEffect(() => {
    filterStudents();
    // eslint-disable-next-line
  }, [students, search, filters]);

  // Distinct section values for the Section filter dropdown — sourced from every
  // class defined for the school (not just sections already assigned to a loaded
  // student), so newly created sections like C/D show up immediately.
  const sectionOptions = useMemo(() => {
    const values = new Set(schoolSections);
    students.forEach((s) => {
      if (s.section) values.add(s.section);
      if (Array.isArray(s.enrolled_classes)) {
        s.enrolled_classes.forEach((c) => { if (c.section) values.add(c.section); });
      }
    });
    return Array.from(values).sort();
  }, [students, schoolSections]);

  // Filter students based on search and filter criteria
  const filterStudents = () => {
    const filtered = students.filter((s) => {
      // Text search
      const matchesSearch = !search ||
        (s.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (s.email || "").toLowerCase().includes(search.toLowerCase());

      // Status filter
      const matchesStatus = !filters.status || s.status === filters.status;

      // Grade filter
      const grade = s.grade || s.class_grade || "";
      const matchesGrade = !filters.grade || grade === filters.grade;

      // Section filter (checks both the flat profile field and real class enrollment)
      const enrolledSections = Array.isArray(s.enrolled_classes)
        ? s.enrolled_classes.map((c) => c.section).filter(Boolean)
        : [];
      const matchesSection = !filters.section ||
        s.section === filters.section ||
        enrolledSections.includes(filters.section);

      // School filter
      const matchesSchool = !filters.school || s.school_name === filters.school;

      return matchesSearch && matchesStatus && matchesGrade && matchesSection && matchesSchool;
    });

    setFilteredStudents(filtered);
  };

  // Fetch available classes for bulk upload based on selected grade
  const fetchAvailableClassesForBulk = async (grade) => {
    if (!grade) {
      setAvailableClassesForBulk([]);
      return;
    }
    setLoadingBulkClasses(true);
    try {
      const resp = await fetch(`${server_url}/admin/available-classes-by-grade/${grade}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await resp.json();
      setAvailableClassesForBulk(Array.isArray(data) ? data : []);
    } catch (e) {
      setAvailableClassesForBulk([]);
      console.error("Failed to fetch available classes:", e);
    } finally {
      setLoadingBulkClasses(false);
    }
  };

  useEffect(() => {
    if (bulkGrade) {
      fetchAvailableClassesForBulk(bulkGrade);
    }
  }, [bulkGrade]);

  // Fetch students
  const fetchStudents = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!accessToken) {
        throw new Error("Authentication token not found");
      }

      const resp = await fetch(`${server_url}/admin/get-students`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch students: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();
      setStudents(Array.isArray(data) ? data : []);
      setFilteredStudents(Array.isArray(data) ? data : []);
      setError(null);
      setErrorModalVisible(false);
    } catch (err) {
      console.error("Error fetching students:", err);
      setError(err.message || "Error fetching students");
      throw err; // Re-throw for retry logic
    } finally {
      setLoading(false);
    }
  };

  // Sync every student's class enrollments to match their current grade + section,
  // in one API call — enrolls into matching classes and removes mismatched ones.
  const handleAutoAssignClasses = async () => {
    if (!window.confirm("This will sync every student's classes to match their current Grade and Section — enrolling them in matching classes and removing any classes that no longer match. Continue?")) return;
    setAutoAssigning(true);
    try {
      const resp = await fetch(`${server_url}/admin/students/auto-assign-classes`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Failed to auto-assign students.");

      setAutoAssignResult(data);
      setAutoAssignModalOpen(true);
      fetchStudents();
    } catch (err) {
      showNotification("error", err.message || "Failed to auto-assign students.");
    } finally {
      setAutoAssigning(false);
    }
  };

  // Calculate statistics for the dashboard cards
  const totalStudents = students.length;
  const recentStudents = students.filter(s => {
    if (!s.created_at) return false;
    const createdDate = new Date(s.created_at);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return createdDate >= thirtyDaysAgo;
  }).length;
  const totalGrades = [...new Set(students.map(s => s.grade || s.class_grade).filter(Boolean))].length;
  const studentsWithProfiles = students.filter(s => s.profile_picture_url).length;

  const totalPages = Math.ceil(filteredStudents.length / pageSize);
  const pagedStudents = filteredStudents.slice((page - 1) * pageSize, page * pageSize);

  // StatsCard component - exactly like LessonPlanHistory.js
  const StatsCard = ({ title, value, icon: Icon, color, bgColor, subtitle }) => (
    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
          </div>
          <div className="p-3 rounded-full" style={{ backgroundColor: bgColor }}>
            <Icon className="w-6 h-6" style={{ color: color }} />
          </div>
        </div>
      </div>
    </div>
  );

  // Dashboard stats adjusted to use the design pattern of LessonPlanHistory.js
  const dashboardStats = [
    {
      icon: Users,
      title: "Total Students",
      value: totalStudents,
      color: colors.blue,
      bgColor: `${colors.blue}10`
    },
    {
      icon: UserCheck,
      title: "Recent Registrations",
      value: recentStudents,
      subtitle: "Last 30 days",
      color: colors.green,
      bgColor: `${colors.green}10`
    },
    {
      icon: Award,
      title: "Active Grades",
      value: totalGrades,
      subtitle: "Different grade levels",
      color: colors.purple,
      bgColor: `${colors.purple}10`
    },
    {
      icon: TrendingUp,
      title: "Completed Profiles",
      value: studentsWithProfiles,
      subtitle: `${Math.round((studentsWithProfiles / totalStudents) * 100) || 0}% completion`,
      color: colors.orange,
      bgColor: `${colors.orange}10`
    }
  ];

  // Bulk Selection Handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(pagedStudents.map(s => s.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  // Handle filter changes - matching LessonPlanHistory.js style
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(1); // Reset to first page when filters change
  };

  // Clear all filters - matching LessonPlanHistory.js style
  const clearFilters = () => {
    setFilters({
      status: "",
      grade: "",
      section: "",
      school: ""
    });
    setSearch("");
    setPage(1);
  };

  // Retry fetching students
  const handleRetryFetch = () => {
    setRetryCount(0); // Reset retry count
    setErrorModalVisible(false); // Close modal
  };

  // Show notification - matching LessonPlanHistory.js
  // Export the whole filtered set, not just the page on screen.
  function handleExportCsv() {
      const rows = filteredStudents;
      if (!rows.length) return showNotification("error", "There is nothing to export.");
      const count = exportToCsv("students", studentCsvColumns, rows);
      showNotification("success", `Exported ${count} students to CSV.`);
  }

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Format date for display - matching LessonPlanHistory.js
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Create Student Handler
  async function handleCreateStudent(e) {
    e.preventDefault();
    try {
      const fd = new FormData();
      fd.append("name", formStudent.name);
      fd.append("email", formStudent.email);
      fd.append("password", formStudent.password);
      fd.append("role", "student");
      fd.append("grade", formStudent.grade);
      if (formStudent.section) fd.append("section", formStudent.section);
      const resp = await fetch(`${server_url}/admin/create-user`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Failed to create student");
      setAddModalOpen(false);
      setFormStudent({ name: "", email: "", password: "", grade: "", section: "" });
      fetchStudents();
      showNotification("success", "Student created successfully!");
    } catch (err) {
      showNotification("error", err.message || "Failed to create student");
    }
  }

  // The server parses CSV quotes, grades and account IDs exactly as upload does.
  async function validateStudentCsv(file, selectedGrade) {
    const requestId = ++csvValidationRequest.current;
    setValidating(true);
    setCSVValidation(null);
    setCSVPreview([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("role", "student");
      if (selectedGrade) fd.append("grade", selectedGrade);
      const resp = await fetch(`${server_url}/admin/bulk-operations/validate-csv`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "CSV validation failed.");
      if (requestId !== csvValidationRequest.current) return;
      setCSVValidation(data);
      setCSVPreview(data.preview || []);
    } catch (err) {
      if (requestId !== csvValidationRequest.current) return;
      showNotification("error", err.message || "Failed to validate CSV file.");
      setCSVFile(null);
    } finally {
      if (requestId === csvValidationRequest.current) setValidating(false);
    }
  }

  async function handleCSVChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setCSVFile(file);
    setUploadResult(null);
    await validateStudentCsv(file, bulkGrade);
  }

  async function handleBulkUpload(e) {
    e.preventDefault();
    if (!csvFile) return showNotification("error", "No CSV file selected.");

    const fd = new FormData();
    fd.append("role", "student");
    fd.append("file", csvFile);
    if (bulkGrade) fd.append("grade", bulkGrade);
    if (bulkSection.trim()) {
      fd.append("section", bulkSection.trim());
    }
    if (selectedClassesForBulk.length > 0) {
      fd.append("class_ids", JSON.stringify(selectedClassesForBulk));
    }

    try {
      const resp = await fetch(`${server_url}/admin/create-user-bulk`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Bulk upload failed.");

      // Store result for display
      setUploadResult(data);

      // Show success with details
      let successMsg = `Created ${data.created_count || 0}, updated ${data.updated_count || 0} students`;
      if (data.parents_linked) successMsg += `; linked ${data.parents_linked} to parents`;
      if (data.parents_created?.length) successMsg += `; created ${data.parents_created.length} parents`;
      if (data.skipped_count > 0) {
        successMsg += `. ${data.skipped_count} records were skipped.`;
      }
      if (data.parent_link_warnings?.length) {
        successMsg += ` ${data.parent_link_warnings.length} parent links need attention.`;
      }

      setCSVModalOpen(false);
      setCSVFile(null);
      setCSVPreview([]);
      setBulkGrade("");
      setBulkSection("");
      setSelectedClassesForBulk([]);
      setAvailableClassesForBulk([]);
      setCSVValidation(null);
      fetchStudents();

      if (data.skipped_count > 0 || data.parent_link_warnings?.length) {
        showNotification("warning", successMsg);
        // Show skipped modal if there are skipped records
        setShowSkippedModal(true);
      } else {
        showNotification("success", successMsg);
      }
    } catch (err) {
      showNotification("error", err.message || "Bulk upload failed.");
    }
  }


  // Bulk Assign Classes Handler
  async function handleBulkAssignClasses(e) {
    e.preventDefault();
    if (selectedStudents.length === 0) return showNotification("error", "No students selected.");
    if (!selectedClasses.length) return showNotification("error", "Please select classes to assign.");

    try {
      const resp = await fetch(`${server_url}/admin/bulk-assign-classes`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          student_ids: selectedStudents,
          class_ids: selectedClasses
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Bulk class assignment failed.");
      setAssignModalOpen(false);
      setSelectedClasses([]);
      setAvailableClasses([]);
      setSelectedStudents([]);
      setSelectAll(false);
      fetchStudents();
      showNotification("success", `Classes assigned to ${selectedStudents.length} students successfully!`);
    } catch (err) {
      showNotification("error", err.message || "Bulk class assignment failed.");
    }
  }

  // Bulk Edit Handler
  async function handleBulkEdit(e) {
    e.preventDefault();
    if (selectedStudents.length === 0) return showNotification("error", "No students selected.");
    if (!bulkEditGrade) return showNotification("error", "Please select a grade.");

    try {
      const resp = await fetch(`${server_url}/admin/bulk-update-students`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          student_ids: selectedStudents,
          grade: bulkEditGrade
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Bulk edit failed.");
      setBulkEditModalOpen(false);
      setBulkEditGrade("");
      setSelectedStudents([]);
      setSelectAll(false);
      fetchStudents();
      showNotification("success", `${selectedStudents.length} students updated successfully!`);
    } catch (err) {
      showNotification("error", err.message || "Bulk edit failed.");
    }
  }

  // Delete confirmation handlers
  const openDeleteConfirmation = (type, email = '', students = []) => {
    setDeleteConfirmModal({
      open: true,
      type,
      email,
      students,
      step: 1
    });
  };

  const handleDeleteConfirmation = async () => {
    const { type, email, students, step } = deleteConfirmModal;

    if (step < 3) {
      setDeleteConfirmModal(prev => ({ ...prev, step: prev.step + 1 }));
      return;
    }

    try {
      if (type === 'single') {
        const resp = await fetch(`${server_url}/admin/delete-user/${email}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || "Failed to delete student");
        showNotification("success", "Student deleted successfully");
      } else if (type === 'bulk') {
        const resp = await fetch(`${server_url}/admin/bulk-delete-students`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ student_ids: students }),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || "Failed to delete students");
        setSelectedStudents([]);
        setSelectAll(false);
        showNotification("success", `${students.length} students deleted successfully`);
      }
      setDeleteConfirmModal({ open: false, type: 'single', email: '', students: [], step: 1 });
      fetchStudents();
    } catch (err) {
      showNotification("error", err.message || "Delete failed");
    }
  };

  // Edit Profile Handlers
  function openEditProfile(student) {
    setEditProfileData({
      grade: student?.grade || student?.class_grade || "",
      class_grade: student?.class_grade || "",
      date_of_birth: student?.date_of_birth ? String(student.date_of_birth).slice(0, 10) : "",
      number_of_siblings: student?.number_of_siblings ?? "",
      siblings_age: Array.isArray(student?.siblings_age) ? student.siblings_age.join(", ") : (student?.siblings_age || ""),
      section: student?.section || "",
      school_name: student?.school_name || "",
      mother_name: student?.mother_name || "",
      father_name: student?.father_name || "",
      phone: student?.phone || "",
      address: student?.address || "",
    });
    setEditProfileMode(true);
  }

  async function handleSaveProfile() {
    // Client-side validation
    const siblingsRaw = editProfileData.siblings_age;
    let siblings_age = undefined;
    if (siblingsRaw !== "" && siblingsRaw !== undefined) {
      const items = String(siblingsRaw).split(",").map(s => s.trim()).filter(Boolean);
      const parsed = items.map(Number);
      if (parsed.some(n => isNaN(n) || !Number.isInteger(n) || n < 0)) {
        message.error("Siblings age must be comma-separated non-negative integers (e.g. 5, 8, 12)");
        return;
      }
      siblings_age = parsed;
    }
    const numSiblings = editProfileData.number_of_siblings;
    if (numSiblings !== "" && numSiblings !== undefined && (isNaN(Number(numSiblings)) || Number(numSiblings) < 0)) {
      message.error("Number of siblings must be a non-negative integer");
      return;
    }

    const payload = {};
    if (editProfileData.grade) payload.grade = editProfileData.grade;
    if (editProfileData.class_grade) payload.class_grade = editProfileData.class_grade;
    if (editProfileData.date_of_birth) payload.date_of_birth = editProfileData.date_of_birth;
    if (numSiblings !== "" && numSiblings !== undefined) payload.number_of_siblings = Number(numSiblings);
    if (siblings_age !== undefined) payload.siblings_age = siblings_age;
    if (editProfileData.section) payload.section = editProfileData.section;
    if (editProfileData.school_name) payload.school_name = editProfileData.school_name;
    if (editProfileData.mother_name) payload.mother_name = editProfileData.mother_name;
    if (editProfileData.father_name) payload.father_name = editProfileData.father_name;
    if (editProfileData.phone) payload.phone = editProfileData.phone;
    if (editProfileData.address) payload.address = editProfileData.address;

    setSavingProfile(true);
    try {
      const resp = await fetch(`${server_url}/admin/students/${selectedStudent.id}/profile`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.detail || "Failed to update profile");
      }
      message.success("Profile updated successfully");
      setEditProfileMode(false);
      fetchStudents(); // refresh list
    } catch (e) {
      message.error(e.message || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  }

  // Assign Classes Modal Handlers
  async function openAssignClasses(student) {
    setSelectedStudent(student);
    setAssignModalOpen(true);
    setSelectedClasses([]);
    setAvailableClasses([]);
    setAssignSectionFilter("");
    setAssigning(true);
    try {
      const resp = await fetch(
        `${server_url}/admin/student/${student.id}/available-classes`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const classes = await resp.json();
      setAvailableClasses(Array.isArray(classes) ? classes : []);
    } catch (e) {
      setAvailableClasses([]);
      showNotification("error", "Failed to fetch classes.");
    } finally {
      setAssigning(false);
    }
  }

  // Bulk Assign Classes Modal Handler
  async function openBulkAssignClasses() {
    if (selectedStudents.length === 0) return showNotification("error", "No students selected.");

    const firstStudent = students.find(s => selectedStudents.includes(s.id));
    if (!firstStudent || !firstStudent.grade) return showNotification("error", "Selected students must have grades assigned.");

    setSelectedStudent({ name: `${selectedStudents.length} Selected Students`, grade: firstStudent.grade });
    setAssignModalOpen(true);
    setSelectedClasses([]);
    setAvailableClasses([]);
    setAssignSectionFilter("");
    setAssigning(true);

    try {
      const resp = await fetch(
        `${server_url}/admin/available-classes-by-grade/${firstStudent.grade}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const classes = await resp.json();
      setAvailableClasses(Array.isArray(classes) ? classes : []);
    } catch (e) {
      setAvailableClasses([]);
      showNotification("error", "Failed to fetch classes.");
    } finally {
      setAssigning(false);
    }
  }

  async function handleAssignClasses(e) {
    e.preventDefault();
    if (!selectedClasses.length) return;
    setAssigning(true);

    try {
      if (selectedStudents.length > 0) {
        await handleBulkAssignClasses(e);
      } else if (selectedStudent && selectedStudent.id) {
        const resp = await fetch(
          `${server_url}/admin/student/${selectedStudent.id}/assign-classes`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ class_ids: selectedClasses }),
          }
        );
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || "Failed to assign classes.");
        setAssignModalOpen(false);
        setAvailableClasses([]);
        setSelectedClasses([]);
        fetchStudents();
        showNotification("success", "Classes assigned successfully!");
      }
    } catch (e) {
      showNotification("error", e.message || "Failed to assign classes.");
    } finally {
      setAssigning(false);
    }
  }

  // Promote Student Handlers
  function openPromote(student) {
    setSelectedStudent(student);
    setPromoteGrade("");
    setPromoteModalOpen(true);
  }

  async function handlePromote(e) {
    e.preventDefault();
    try {
      const fd = new FormData();
      fd.append("new_grade", promoteGrade);
      const resp = await fetch(
        `${server_url}/admin/student/${selectedStudent.id}/promote`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${accessToken}` },
          body: fd,
        }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Failed to promote.");
      setPromoteModalOpen(false);
      fetchStudents();
      showNotification("success", "Student promoted successfully!");
    } catch (err) {
      showNotification("error", err.message || "Failed to promote.");
    }
  }

  // CSV Download Template
  function downloadCSVTemplate() {
    // `parent_email` links the student to a guardian. Siblings sharing an email
    // are linked to one parent account, which is created if it does not exist.
    const csv = buildCsvText(studentCsvColumns, studentCsvTemplateRows);
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = window.URL.createObjectURL(blob);
    a.download = "students_template.csv";
    a.click();
  }

  // ============ BULK PROFILE UPDATE HANDLERS ============

  // Fetch profile template info
  async function fetchProfileTemplateInfo() {
    try {
      const resp = await fetch(`${server_url}/admin/bulk-update-student-profiles/template`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setProfileTemplateInfo(data);
      }
    } catch (err) {
      console.error("Failed to fetch profile template info:", err);
    }
  }

  // Download profile CSV template
  function downloadProfileCSVTemplate() {
    // Comprehensive template with all major profile fields
    const headers = [
      "email",
      // Basic & School Info
      "name", "school_name", "village", "brc", "crc", "state", "pin_code", "udise_code", "teacher_code", "apaar_id",
      // Personal Info
      "roll_no", "registration_no", "class_grade", "section", "date_of_birth", "birthday", "address", "phone", "age_years",
      // Family Info
      "mother_name", "father_name", "mother_education", "father_education", "mother_occupation", "father_occupation", "number_of_siblings", "siblings_age",
      // Language & Locale
      "mother_tongue", "medium_of_instruction", "rural_or_urban",
      // Health
      "times_fallen_ill", "attendance_low_reason",
      // About Me
      "live_with", "stay_at", "free_time_activity", "do_very_well", "responsible", "could_do_better", "show_care_by", "proud_when",
      "good_at", "not_good_at", "would_like_to_improve", "like_to", "dont_like_to",
      // Favourites
      "favourite_colours", "favourite_foods", "favourite_games", "favourite_festivals", "favourite_flowers",
      "favourite_sports", "favourite_animals", "favourite_subjects", "want_to_be", "idol", "friends_are",
      // Goals & Ambitions
      "academic_goal", "academic_goal_importance", "academic_goal_step1", "academic_goal_step2",
      "personal_goal", "personal_goal_importance", "personal_goal_step1", "personal_goal_step2",
      "ambition", "skills_to_achieve", "achieve_by", "focus_subjects", "habits_for_ambition",
      "guidance_from", "help_by_person", "learn_new", "feel_when_achieved", "parents_feel_when_achieved",
      // Learnings
      "learnings_school", "learnings_outside_school", "teacher_help_with", "teacher_help_details",
      "teacher_to_know", "teacher_know_details", "learn_targets"
    ].join(",");
    
    // Example row with sample data
    const exampleRow = [
      "student@example.com",
      // Basic & School Info
      "John Doe", "ABC Public School", "Village Name", "BRC Name", "CRC Name", "State Name", "123456", "12345678901", "TC001", "APAAR123",
      // Personal Info
      "001", "REG001", "5", "A", "2015-05-10", "2015-05-10", "123 Main Street", "9876543210", "10",
      // Family Info
      "Mother Name", "Father Name", "Graduate", "Post Graduate", "Teacher", "Engineer", "2", "8,12",
      // Language & Locale
      "Hindi", "English", "Urban",
      // Health
      "2", "Seasonal illness",
      // About Me
      "Parents", "Own house", "Reading books", "Mathematics", "Homework", "Sports", "Helping others", "Good grades",
      "Drawing", "Public speaking", "Communication", "Playing games", "Waking up early",
      // Favourites
      "Blue,Green", "Pizza,Ice cream", "Cricket,Chess", "Diwali,Holi", "Rose,Lily",
      "Cricket,Football", "Dog,Cat", "Math,Science", "Doctor", "My father", "Rahul,Priya",
      // Goals & Ambitions
      "Score 90% in exams", "To get into good college", "Study daily", "Practice problems",
      "Learn swimming", "For fitness", "Join classes", "Practice weekly",
      "Become a doctor", "Medical,Biology", "By age 25", "Science,Biology", "Study daily,Exercise",
      "Parents", "Guidance and support", "New medical techniques", "Proud and accomplished", "Very happy",
      // Learnings
      "Math,Science,English", "Music,Dance", "Difficult topics", "Need extra classes",
      "I am hardworking", "I complete homework on time", "Learn coding,Learn music"
    ].join(",");
    
    const csv = `${headers}\n${exampleRow}`;
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = window.URL.createObjectURL(blob);
    a.download = "student_profiles_template.csv";
    a.click();
  }

  // Handle profile CSV file change
  async function handleProfileCSVChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setProfileCsvFile(file);
    setProfileValidation(null);
    setProfileUploadResult(null);

    const reader = new FileReader();
    reader.onload = async (evt) => {
      const text = evt.target.result;
      const lines = text.split("\n").filter(line => line.trim());
      if (lines.length < 2) {
        setProfileCsvPreview([]);
        return;
      }

      const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
      const emailIdx = headers.indexOf("email");

      if (emailIdx === -1) {
        showNotification("error", "CSV must have an 'email' column to identify students.");
        setProfileCsvFile(null);
        setProfileCsvPreview([]);
        return;
      }

      // Parse preview rows
      const parsedRows = [];
      for (let i = 1; i < lines.length && i <= 20; i++) {
        const cols = lines[i].split(",").map(c => c.trim());
        const email = cols[emailIdx] || "";
        const fieldsCount = cols.filter((c, idx) => idx !== emailIdx && c).length;

        parsedRows.push({
          row: i + 1,
          email,
          fields_count: fieldsCount,
          status: "pending",
          reason: null
        });
      }

      setProfileCsvPreview(parsedRows);

      // Validate on server
      if (file) {
        setValidatingProfile(true);
        try {
          const fd = new FormData();
          fd.append("file", file);

          const resp = await fetch(`${server_url}/admin/bulk-update-student-profiles/validate`, {
            method: "POST",
            headers: { Authorization: `Bearer ${accessToken}` },
            body: fd,
          });

          if (resp.ok) {
            const validation = await resp.json();
            setProfileValidation(validation);

            // Update preview with server validation results
            if (validation.preview) {
              setProfileCsvPreview(validation.preview);
            }
          } else {
            const errorData = await resp.json().catch(() => ({}));
            const errorMsg = errorData.detail || "CSV validation failed. Please check your file format.";
            showNotification("error", errorMsg);
            setProfileCsvFile(null);
            setProfileCsvPreview([]);
            setProfileValidation(null);
          }
        } catch (err) {
          console.error("Profile validation error:", err);
          showNotification("error", "Failed to validate CSV file.");
        } finally {
          setValidatingProfile(false);
        }
      }
    };
    reader.readAsText(file);
  }

  // Handle bulk profile upload
  async function handleBulkProfileUpload(e) {
    e.preventDefault();
    if (!profileCsvFile) return showNotification("error", "No CSV file selected.");

    const fd = new FormData();
    fd.append("file", profileCsvFile);

    try {
      const resp = await fetch(`${server_url}/admin/bulk-update-student-profiles`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Bulk profile update failed.");

      // Store result for display
      setProfileUploadResult(data);

      // Show success with details
      let successMsg = `Updated ${data.updated_count || 0} student profiles`;
      if (data.skipped_count > 0) {
        successMsg += `. ${data.skipped_count} records were skipped.`;
      }

      setProfileUploadModalOpen(false);
      setProfileCsvFile(null);
      setProfileCsvPreview([]);
      setProfileValidation(null);
      fetchStudents(); // Refresh student list

      if (data.skipped_count > 0) {
        showNotification("warning", successMsg);
        setShowProfileSkippedModal(true);
      } else {
        showNotification("success", successMsg);
      }
    } catch (err) {
      showNotification("error", err.message || "Bulk profile update failed.");
    }
  }

  // Open profile upload modal
  function openProfileUploadModal() {
    setProfileUploadModalOpen(true);
    setProfileCsvFile(null);
    setProfileCsvPreview([]);
    setProfileValidation(null);
    setProfileUploadResult(null);
    fetchProfileTemplateInfo();
  }

  // UI
  return (
    <>
      <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

          {/* Notification - exactly like LessonPlanHistory.js */}
          {notification && (
            <div
              className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 ${notification.type === 'success' ? 'bg-green-500 text-white' :
                notification.type === 'error' ? 'bg-red-500 text-white' :
                  'bg-blue-500 text-white'
                }`} style={{ animation: 'slideInRight 0.5s ease-out' }}>
              {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> :
                notification.type === 'error' ? <AlertTriangle className="w-5 h-5" /> :
                  <AlertTriangle className="w-5 h-5" />}
              <span className="font-[poppins]">{notification.message}</span>
              <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-80">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Header - exactly like LessonPlanHistory.js */}
          <div className="pt-8 pb-8 text-center relative overflow-hidden">
            <div className="relative z-10">
              <div>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                  style={{ backgroundColor: '#D67C2C' }}>
                  <GraduationCap className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-5xl font-bold bg-[#D67C2C] bg-clip-text text-transparent mb-4">
                  Student Management Dashboard
                </h1>
                <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                  Comprehensive student management system with advanced tracking and bulk operations
                </p>
              </div>
            </div>
          </div>

          {/* Dashboard Cards - exactly like LessonPlanHistory.js */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {dashboardStats.map((stat, index) => (
              <StatsCard
                key={index}
                title={stat.title}
                value={stat.value}
                icon={stat.icon}
                color={stat.color}
                bgColor={stat.bgColor}
                subtitle={stat.subtitle}
              />
            ))}
          </div>

          {/* Filter Section - exactly like LessonPlanHistory.js */}
          <div className="bg-white rounded-2xl shadow-md overflow-visible border border-gray-200 mb-8">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                    <Filter className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Filter Students</h2>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setAddModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Create New Student
                  </button>

                  <ActionMenu
                    label="Bulk Actions"
                    icon={<Layers className="w-5 h-5" />}
                    items={[
                      {
                        key: "upload",
                        label: "Bulk Upload (CSV)",
                        description: "Create students from a CSV, or update those that already exist.",
                        icon: <Upload className="w-4 h-4" />,
                        tone: "primary",
                        onClick: () => setCSVModalOpen(true),
                      },
                      {
                        key: "export",
                        label: "Export CSV",
                        description: "Download every student matching the current filters.",
                        icon: <Download className="w-4 h-4" />,
                        tone: "success",
                        onClick: handleExportCsv,
                      },
                      {
                        key: "profiles",
                        label: "Bulk Update Profiles",
                        description: "Update profile fields for existing students by email.",
                        icon: <FileText className="w-4 h-4" />,
                        tone: "info",
                        onClick: openProfileUploadModal,
                      },
                      {
                        key: "auto-assign",
                        label: autoAssigning ? "Auto-Assigning…" : "Auto-Assign to Classes",
                        description: "Place students into classes matching their grade and section.",
                        icon: autoAssigning
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Wand2 className="w-4 h-4" />,
                        tone: "violet",
                        disabled: autoAssigning,
                        onClick: handleAutoAssignClasses,
                      },
                    ]}
                  />
                </div>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={(e) => e.preventDefault()}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-2">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <Search className="inline-block w-4 h-4 mr-2" />
                    Search
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search by name or email..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                    />
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                  </div>
                </div>

                {/*<div className="space-y-2">*/}
                {/*  <label className="block text-sm font-medium text-gray-700">*/}
                {/*    <CheckCircle className="inline-block w-4 h-4 mr-2" />*/}
                {/*    Status*/}
                {/*  </label>*/}
                {/*  <select*/}
                {/*    name="status"*/}
                {/*    value={filters.status}*/}
                {/*    onChange={handleFilterChange}*/}
                {/*    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"*/}
                {/*  >*/}
                {/*    <option value="">All Statuses</option>*/}
                {/*    <option value="active">Active</option>*/}
                {/*    <option value="inactive">Inactive</option>*/}
                {/*    <option value="new">New</option>*/}
                {/*  </select>*/}
                {/*</div>*/}

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <GraduationCap className="inline-block w-4 h-4 mr-2" />
                    Grade
                  </label>
                  <select
                    name="grade"
                    value={filters.grade}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  >
                    <option value="">All Grades</option>
                    {GRADE_LIST.map((g) => (
                      <option key={g} value={g}>Grade {g}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <BookOpen className="inline-block w-4 h-4 mr-2" />
                    Section
                  </label>
                  <select
                    name="section"
                    value={filters.section}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  >
                    <option value="">All Sections</option>
                    {sectionOptions.map((sec) => (
                      <option key={sec} value={sec}>Section {sec}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-3 lg:col-span-6 flex items-center justify-center space-x-4 mt-4">
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Search className="inline-block w-4 h-4 mr-2" />
                    Search
                  </button>

                  <button
                    type="button"
                    onClick={clearFilters}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <RefreshCw className="inline-block w-4 h-4 mr-2" />
                    Clear Filters
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {selectedStudents.length > 0 && (
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
              <div className="p-6 bg-orange-50 border-b border-orange-100">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="text-orange-700 font-semibold">
                      {selectedStudents.length} student{selectedStudents.length > 1 ? 's' : ''} selected
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => openBulkAssignClasses()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#D67C2C] text-white font-medium rounded-lg hover:bg-[#c06a25] transition-colors shadow-md"
                    >
                      <BookOpen className="w-4 h-4" />
                      Assign Classes
                    </button>
                    <button
                      onClick={() => setBulkEditModalOpen(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition-colors shadow-md"
                    >
                      <Edit3 className="w-4 h-4" />
                      Bulk Edit
                    </button>
                    <button
                      onClick={() => openDeleteConfirmation('bulk', '', selectedStudents)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors shadow-md"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                    <button
                      onClick={() => {
                        setSelectedStudents([]);
                        setSelectAll(false);
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors shadow-md"
                    >
                      <X className="w-4 h-4" />
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Students Table Section */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">
                  Students ({filteredStudents.length})
                </h2>
              </div>
            </div>

            {/* Table Content */}
            {loading ? (
              <div className="flex justify-center items-center p-12">
                <div className="text-center">
                  <div className="w-8 h-8 text-[#D67C2C] animate-spin rounded-full border-4 border-orange-500 border-t-transparent mx-auto"></div>
                  <p className="mt-4 text-gray-600 font-medium">Loading students...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex justify-center items-center p-12 text-red-500">
                <AlertTriangle className="w-6 h-6 mr-2" />
                <span>{error}</span>
                <button
                  onClick={handleRetryFetch}
                  className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : filteredStudents.length === 0 ? (
              <div className="flex flex-col justify-center items-center p-12 text-gray-500">
                <User2 className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-lg mb-3">No students found</p>
                <p className="text-sm text-gray-400 mb-6">
                  {search || filters.status || filters.grade || filters.school ?
                    "Try clearing your filters or creating a new student." :
                    "Get started by creating your first student."}
                </p>
                <button
                  onClick={() => setAddModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Create New Student
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button onClick={handleSelectAll} className="flex items-center gap-2 hover:text-[#D67C2C] transition-colors">
                          {selectAll ? <CheckSquare className="w-5 h-5 text-[#D67C2C]" /> : <Square className="w-5 h-5" />}
                          Select
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Grade
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Section
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Classes
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pagedStudents.map((s) => (
                      <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap align-top">
                          <div className="flex items-center h-12">
                            <button
                              onClick={() => handleSelectStudent(s.id)}
                              className="hover:text-[#D67C2C] transition-colors"
                            >
                              {selectedStudents.includes(s.id) ?
                                <CheckSquare className="w-5 h-5 text-[#D67C2C]" /> :
                                <Square className="w-5 h-5 text-gray-400" />
                              }
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap align-top">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-12 w-12 rounded-lg flex items-center justify-center bg-orange-50 overflow-hidden">
                              {s.profile_picture_url ? (
                                <img
                                  src={server_url + s.profile_picture_url}
                                  alt={s.name}
                                  className="h-12 w-12 rounded-lg object-cover"
                                />
                              ) : (
                                <User2 className="h-5 w-5 text-[#D67C2C]" />
                              )}
                            </div>
                            <div className="ml-4 min-w-0">
                              <div className="text-sm font-semibold text-gray-900 truncate">
                                {s.name || 'Unnamed Student'}
                              </div>
                              <div className="text-sm text-gray-500 truncate" title={s.email}>
                                {s.email || 'No email'}
                              </div>
                              {s.roll_no && (
                                <div className="text-xs text-gray-400">Roll {s.roll_no}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap align-top">
                          <div className="flex items-center h-12">
                            <span className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-xs font-medium">
                              <Award className="w-3 h-3 mr-1" />
                              {s.grade || s.class_grade || "—"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 align-top">
                          <div className="flex items-center h-12">
                            {s.section || "—"}
                          </div>
                        </td>
                        <td className="px-6 py-4 align-top">
                          <div className="flex items-center min-h-[3rem]">
                            {Array.isArray(s.enrolled_classes) && s.enrolled_classes.length > 0 ? (
                              <div className="flex flex-wrap gap-1 max-w-[220px]">
                                {s.enrolled_classes.slice(0, 2).map((c) => (
                                  <span
                                    key={c.class_id}
                                    title={c.teacher_name ? `Teacher: ${c.teacher_name}` : undefined}
                                    className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium whitespace-nowrap"
                                  >
                                    <BookOpen className="w-3 h-3 mr-1" />
                                    {c.grade}{c.section ? ` - ${c.section}` : ''}
                                  </span>
                                ))}
                                {s.enrolled_classes.length > 2 && (
                                  <span
                                    title={s.enrolled_classes.slice(2).map((c) => `${c.grade}${c.section ? ` - ${c.section}` : ''}`).join(', ')}
                                    className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium whitespace-nowrap"
                                  >
                                    +{s.enrolled_classes.length - 2} more
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">Not assigned</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium align-top">
                          <ActionsCell className="justify-end h-12">
                            <ActionIconButton
                              icon={Eye}
                              tone="view"
                              onClick={() => {
                                setSelectedStudent(s);
                                setDetailModalOpen(true);
                              }}
                              title="View Details"
                            />

                            <ActionIconButton
                              icon={BookOpen}
                              tone="assign"
                              onClick={() => openAssignClasses(s)}
                              title="Assign Classes"
                            />

                            <ActionIconButton
                              icon={ArrowUpCircle}
                              tone="approve"
                              onClick={() => openPromote(s)}
                              title="Promote"
                            />

                            <ActionIconButton
                              icon={Trash2}
                              tone="delete"
                              onClick={() => openDeleteConfirmation('single', s.email)}
                              title="Delete"
                            />
                          </ActionsCell>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination - exactly like LessonPlanHistory.js */}
            {!loading && !error && filteredStudents.length > 0 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-sm text-gray-500">
                    Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, filteredStudents.length)} of {filteredStudents.length} students
                  </div>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                    className="text-sm border border-gray-300 rounded px-2 py-1 text-gray-600"
                  >
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPage(Math.max(page - 1, 1))}
                    disabled={page === 1}
                    className={`p-2 rounded-md ${page === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                    // Show pages around current page
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }

                    return (
                      <button
                        key={i}
                        onClick={() => setPage(pageNum)}
                        className={`px-3 py-1 rounded-md ${pageNum === page ? 'bg-[#D67C2C] text-white' : 'text-gray-600 hover:bg-gray-200'}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => setPage(Math.min(page + 1, totalPages))}
                    disabled={page === totalPages}
                    className={`p-2 rounded-md ${page === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Error Modal - similar to LessonPlanHistory.js DeleteConfirmationModal */}
          <Modal
            title={
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                <span>Error Loading Students</span>
              </div>
            }
            open={errorModalVisible}
            onCancel={() => setErrorModalVisible(false)}
            footer={[
              <button
                key="retry"
                onClick={handleRetryFetch}
                className="px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors mr-2"
              >
                Retry
              </button>,
              <button
                key="cancel"
                onClick={() => setErrorModalVisible(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>,
            ]}
          >
            <div className="py-4">
              <p className="mb-4">Failed to fetch students. This could be due to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Network connection issue</li>
                <li>Authentication token not fully loaded</li>
                <li>Server temporarily unavailable</li>
              </ul>
              <p className="mt-4">Please try refreshing the page or click "Retry" to attempt loading again.</p>
            </div>
          </Modal>

          {/* Auto-Assign Classes Summary Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2 text-purple-600">
                <Wand2 className="w-5 h-5" />
                <span>Auto-Assign Summary</span>
              </div>
            }
            open={autoAssignModalOpen}
            onCancel={() => setAutoAssignModalOpen(false)}
            width={720}
            footer={[
              <button
                key="close"
                onClick={() => setAutoAssignModalOpen(false)}
                className="px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors"
              >
                Close
              </button>,
            ]}
          >
            {autoAssignResult && (
              <div className="py-2 space-y-5">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-700">{autoAssignResult.assigned_count}</div>
                    <div className="text-xs text-green-700 mt-1">New Enrollments</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-700">{autoAssignResult.students_updated_count}</div>
                    <div className="text-xs text-blue-700 mt-1">Students Newly Assigned</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-red-700">{autoAssignResult.removed_count}</div>
                    <div className="text-xs text-red-700 mt-1">Mismatched Removed</div>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-purple-700">{autoAssignResult.students_corrected_count}</div>
                    <div className="text-xs text-purple-700 mt-1">Students Corrected</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-700">{autoAssignResult.already_assigned_count}</div>
                    <div className="text-xs text-gray-700 mt-1">Already Correct</div>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-yellow-700">{autoAssignResult.unmatched_count}</div>
                    <div className="text-xs text-yellow-700 mt-1">Unmatched</div>
                  </div>
                </div>

                {Array.isArray(autoAssignResult.changes) && autoAssignResult.changes.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Changes Made</h4>
                    <div className="border border-gray-200 rounded-lg max-h-56 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Student</th>
                            <th className="text-left px-3 py-2 font-medium text-green-700">Added</th>
                            <th className="text-left px-3 py-2 font-medium text-red-700">Removed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {autoAssignResult.changes.map((c, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="px-3 py-2 text-gray-800">{c.name || c.student_id}</td>
                              <td className="px-3 py-2 text-green-700">{c.added.join(", ") || "—"}</td>
                              <td className="px-3 py-2 text-red-700">{c.removed.join(", ") || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {Array.isArray(autoAssignResult.unmatched) && autoAssignResult.unmatched.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Unmatched Students</h4>
                    <div className="border border-gray-200 rounded-lg max-h-56 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Student</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {autoAssignResult.unmatched.map((u, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="px-3 py-2 text-gray-800">{u.name || u.student_id}</td>
                              <td className="px-3 py-2 text-gray-600">{u.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {(!autoAssignResult.changes || autoAssignResult.changes.length === 0) && autoAssignResult.assigned_count === 0 && autoAssignResult.removed_count === 0 && (
                  <p className="text-sm text-gray-600">No changes were needed — every student is already correctly assigned.</p>
                )}
              </div>
            )}
          </Modal>

          {/* Add Student Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#D67C2C]" />
                <span>Add New Student</span>
              </div>
            }
            open={addModalOpen}
            onCancel={() => setAddModalOpen(false)}
            footer={null}
          >
            <form
              className="py-4"
              onSubmit={handleCreateStudent}
            >
              <div className="space-y-5">
                <div>
                  <label className="font-medium block mb-2 text-gray-700">Full Name</label>
                  <input
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors"
                    required
                    placeholder="Enter student's full name"
                    value={formStudent.name}
                    onChange={(e) => setFormStudent((d) => ({ ...d, name: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="font-medium block mb-2 text-gray-700">Email Address</label>
                  <input
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors"
                    required
                    type="email"
                    placeholder="student@example.com"
                    value={formStudent.email}
                    onChange={(e) => setFormStudent((d) => ({ ...d, email: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="font-medium block mb-2 text-gray-700">Password</label>
                  <div className="relative">
                    <input
                      className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 pr-12 focus:outline-none focus:border-[#D67C2C] transition-colors"
                      required
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a strong password"
                      value={formStudent.password}
                      onChange={(e) => setFormStudent((d) => ({ ...d, password: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-3.5 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Share this password with the student securely. No onboarding email is sent.
                  </p>
                </div>

                <div>
                  <label className="font-medium block mb-2 text-gray-700">Grade</label>
                  <select
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors appearance-none bg-white"
                    required
                    value={formStudent.grade}
                    onChange={(e) => setFormStudent((d) => ({ ...d, grade: e.target.value }))}
                  >
                    <option value="">Select grade</option>
                    {GRADE_LIST.map((g) => (
                      <option key={g} value={g}>
                        Grade {g}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="font-medium block mb-2 text-gray-700">Section <span className="text-gray-400 text-sm font-normal">(optional)</span></label>
                  <input
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors"
                    placeholder="e.g. A, B, Red, Blue"
                    value={formStudent.section}
                    onChange={(e) => setFormStudent((d) => ({ ...d, section: e.target.value }))}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setAddModalOpen(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-[#D67C2C] text-white rounded-lg py-3 font-semibold hover:bg-[#c06a25] transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Add Student
                  </button>
                </div>
              </div>
            </form>
          </Modal>

          {/* CSV Upload Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-[#D67C2C]" />
                <span>Bulk Upload Students</span>
              </div>
            }
            open={csvModalOpen}
            onCancel={() => {
              setCSVModalOpen(false);
              setBulkGrade("");
              setBulkSection("");
              setCSVFile(null);
              setCSVPreview([]);
              setCSVValidation(null);
              setSelectedClassesForBulk([]);
              setAvailableClassesForBulk([]);
            }}
            footer={null}
          >
            <form
              className="py-4"
              onSubmit={handleBulkUpload}
            >
              <div className="bg-orange-50 p-4 rounded-lg mb-6">
                <div className="text-[#D67C2C] font-medium mb-2">CSV columns for export and upload:</div>
                <div className="font-mono text-xs bg-white p-2 rounded border border-orange-200 break-all">
                  {studentCsvColumns.map((column) => column.header).join(",")}
                </div>
                <div className="text-sm text-[#D67C2C] mt-2">
                  Name and email are required. Existing students can be re-uploaded with a blank password. New students need a password and a grade in the CSV or selected below.
                  Keep the <span className="font-mono">readonly_id</span> unchanged in an exported roster so existing accounts can be matched safely.
                </div>
                <div className="text-sm text-[#D67C2C] mt-2">
                  Optional: add <span className="font-mono">parent_email</span> to link each student to a guardian.
                  If no parent account exists for that email, you must enter a <span className="font-mono">parent_password</span> to create it.
                  Siblings sharing an email are linked to the same parent.
                  Leave the parent password blank only when that parent already has an account. No onboarding emails are sent to students or parents.
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Grade Selection */}
                <div>
                  <label className="font-medium block mb-2 text-gray-700">Grade for new students (optional if CSV has grade)</label>
                  <select
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors appearance-none bg-white"
                    value={bulkGrade}
                    onChange={(e) => {
                      setBulkGrade(e.target.value);
                      setBulkSection("");
                      if (csvFile) validateStudentCsv(csvFile, e.target.value);
                    }}
                  >
                    <option value="">Select grade</option>
                    {GRADE_LIST.map((g) => (
                      <option key={g} value={g}>
                        Grade {g}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Section Selection */}
                <div>
                  <label className="font-medium block mb-2 text-gray-700">
                    Section <span className="text-gray-400 text-sm font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors bg-white"
                    placeholder="e.g. A, B, Red"
                    value={bulkSection}
                    onChange={(e) => setBulkSection(e.target.value)}
                    maxLength={20}
                  />
                </div>

                {/* File Upload */}
                <div>
                  <label className="font-medium block mb-2 text-gray-700">Upload CSV File *</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVChange}
                    className="w-full border-2 border-dashed border-gray-200 p-3 rounded-lg text-gray-700 focus:outline-none focus:border-[#D67C2C] transition-colors cursor-pointer"
                    required
                  />
                </div>
              </div>

              {/* Available Classes Selection */}
              {bulkGrade && (
                <div className="mb-6">
                  <label className="font-medium block mb-2 text-gray-700">
                    Available Classes for Grade {bulkGrade} (Optional)
                  </label>
                  {loadingBulkClasses ? (
                    <div className="flex items-center gap-2 py-4">
                      <div className="w-5 h-5 animate-spin rounded-full border-2 border-[#D67C2C] border-t-transparent"></div>
                      <span className="text-gray-600">Loading available classes...</span>
                    </div>
                  ) : availableClassesForBulk.length > 0 ? (
                    <div className="border-2 border-gray-200 rounded-lg p-4 max-h-40 overflow-y-auto">
                      <div className="space-y-2">
                        {availableClassesForBulk.map((cls) => (
                          <label key={cls.class_id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                            <input
                              type="checkbox"
                              value={cls.class_id}
                              checked={selectedClassesForBulk.includes(cls.class_id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedClassesForBulk(prev => [...prev, cls.class_id]);
                                } else {
                                  setSelectedClassesForBulk(prev => prev.filter(id => id !== cls.class_id));
                                }
                              }}
                              className="rounded border-gray-300 text-[#D67C2C] focus:ring-[#D67C2C]"
                            />
                            <span className="text-sm">
                              {cls.name}{cls.section ? ` (Section ${cls.section})` : ''}{cls.grade ? ` • Grade ${cls.grade}` : ''}{cls.teacher_name ? ` — ${cls.teacher_name}` : ''}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-500 text-sm bg-gray-50 p-4 rounded-lg">
                      No classes available for this grade yet.
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center gap-4 mb-6">
                <button
                  type="button"
                  onClick={downloadCSVTemplate}
                  className="inline-flex gap-2 text-[#D67C2C] hover:text-[#c06a25] transition-colors items-center text-sm font-medium"
                >
                  <FileDown className="w-4 h-4" /> Download Template CSV
                </button>
              </div>

              {/* Validation Summary */}
              {csvValidation && (
                <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-gray-50">
                  <div className="font-medium text-gray-700 mb-3">Validation Summary</div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span>Valid: <strong>{csvValidation.valid_count || 0}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <span>Invalid: <strong>{csvValidation.invalid_emails?.length || 0}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <span>Duplicates: <strong>{csvValidation.duplicate_emails_in_csv?.length || 0}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                      <span>Role conflicts: <strong>{csvValidation.existing_emails_in_db?.length || 0}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      <span>Roster mismatch: <strong>{csvValidation.roster_mismatches?.length || 0}</strong></span>
                    </div>
                  </div>
                  {csvValidation.summary && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <span className="text-green-600 font-medium">
                        ✓ {csvValidation.summary.can_upload} new, {csvValidation.summary.will_update || 0} existing
                      </span>
                      {csvValidation.summary.will_skip > 0 && (
                        <span className="text-orange-600 ml-4">
                          ⚠ {csvValidation.summary.will_skip} will be skipped
                        </span>
                      )}
                    </div>
                  )}
                  {csvValidation.total_rows > 0 && !csvValidation.summary?.can_upload && !csvValidation.summary?.will_update && (
                    <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-red-700 text-sm">
                      None of these rows match students in this school or qualify as new students. Check that this is a fresh export from this school's Students page. New students need passwords; existing students do not.
                    </div>
                  )}
                  {csvValidation.roster_mismatches?.length > 0 && (
                    <div className="mt-3 rounded border border-purple-200 bg-purple-50 p-3 text-purple-700 text-sm">
                      {csvValidation.roster_mismatches.length} exported roster ID(s) could not be matched safely. Download a fresh roster from this school before editing and re-uploading it.
                    </div>
                  )}
                  {csvValidation.parent_password_issues?.length > 0 && (
                    <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-red-700 text-sm">
                      {csvValidation.parent_password_issues.length} new parent account(s) need a parent_password in the CSV. The upload is blocked until those passwords are entered. No parent login email will be sent.
                    </div>
                  )}
                </div>
              )}

              {/* Validating indicator */}
              {validating && (
                <div className="mb-4 flex items-center gap-2 text-gray-600">
                  <div className="w-4 h-4 animate-spin rounded-full border-2 border-[#D67C2C] border-t-transparent"></div>
                  <span>Validating CSV...</span>
                </div>
              )}

              {/* Enhanced Preview with color-coded rows */}
              {csvPreview.length > 0 && (
                <div className="mb-6">
                  <div className="font-medium mb-2 text-gray-700">Preview (First 20 rows):</div>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm overflow-x-auto max-h-60 border border-gray-200">
                    {/* Header */}
                    <div className="font-mono font-semibold text-gray-700 py-2 border-b-2 border-gray-300 flex gap-4">
                      <span className="w-10">Row</span>
                      <span className="flex-1">Name</span>
                      <span className="flex-1">Email</span>
                      <span className="w-32">Status</span>
                    </div>
                    {/* Data rows */}
                    {csvPreview.map((row, i) => {
                      const statusColors = {
                        valid: "bg-green-50 border-l-4 border-green-500",
                        invalid_email: "bg-red-50 border-l-4 border-red-500",
                        duplicate: "bg-yellow-50 border-l-4 border-yellow-500",
                        update: "bg-blue-50 border-l-4 border-blue-500",
                        invalid_roster: "bg-purple-50 border-l-4 border-purple-500",
                        invalid_parent: "bg-red-50 border-l-4 border-red-500",
                        exists: "bg-orange-50 border-l-4 border-orange-500"
                      };
                      const statusLabels = {
                        valid: <span className="text-green-600">✓ Valid</span>,
                        invalid_email: <span className="text-red-600">✗ {row.reason}</span>,
                        duplicate: <span className="text-yellow-600">⚠ Duplicate</span>,
                        update: <span className="text-blue-600">↻ Update</span>,
                        invalid_roster: <span className="text-purple-600" title={row.reason}>✗ Roster mismatch</span>,
                        invalid_parent: <span className="text-red-600" title={row.reason}>✗ Parent password</span>,
                        exists: <span className="text-orange-600">⚠ Exists</span>
                      };

                      return (
                        <div
                          key={i}
                          className={`font-mono py-2 border-b last:border-0 flex gap-4 items-center ${statusColors[row.status] || ""}`}
                        >
                          <span className="w-10 text-gray-500">{row.row}</span>
                          <span className="flex-1 truncate">{row.name || "-"}</span>
                          <span className="flex-1 truncate">{row.email || "-"}</span>
                          <span className="w-32 text-xs">
                            {statusLabels[row.status] || row.status}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}


              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setCSVModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-[#D67C2C] text-white rounded-lg py-3 font-semibold hover:bg-[#c06a25] transition-colors flex items-center justify-center gap-2"
                  disabled={!csvFile || validating || (csvValidation && (
                    csvValidation.parent_password_issues?.length > 0 ||
                    (!csvValidation.summary?.can_upload && !csvValidation.summary?.will_update)
                  ))}
                >
                  <Upload className="w-5 h-5" />
                  Upload Students
                </button>
              </div>
            </form>
          </Modal>

          {/* Bulk Edit Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-[#D67C2C]" />
                <span>Bulk Edit Students</span>
              </div>
            }
            open={bulkEditModalOpen}
            onCancel={() => setBulkEditModalOpen(false)}
            footer={null}
          >
            <form
              className="py-4"
              onSubmit={handleBulkEdit}
            >
              <div className="mb-6 p-4 bg-orange-50 rounded-lg">
                <div className="text-[#D67C2C] font-medium">
                  {selectedStudents.length} student{selectedStudents.length > 1 ? 's' : ''} selected
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Changes will be applied to all selected students
                </div>
              </div>

              <div className="mb-6">
                <label className="font-medium block mb-2 text-gray-700">New Grade</label>
                <select
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors appearance-none bg-white"
                  required
                  value={bulkEditGrade}
                  onChange={(e) => setBulkEditGrade(e.target.value)}
                >
                  <option value="">Select new grade</option>
                  {GRADE_LIST.map((g) => (
                    <option key={g} value={g}>
                      Grade {g}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setBulkEditModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-[#D67C2C] text-white rounded-lg py-3 font-semibold hover:bg-[#c06a25] transition-colors flex items-center justify-center gap-2"
                  disabled={!bulkEditGrade}
                >
                  <Edit3 className="w-5 h-5" />
                  Update Students
                </button>
              </div>
            </form>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                <span>Confirm Deletion - Step {deleteConfirmModal.step} of 3</span>
              </div>
            }
            open={deleteConfirmModal.open}
            onCancel={() => setDeleteConfirmModal({ open: false, type: 'single', email: '', students: [], step: 1 })}
            footer={null}
          >
            <div className="py-4">
              {deleteConfirmModal.step === 1 && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-8 h-8 text-red-500" />
                  </div>
                  <p className="text-gray-700 mb-4">
                    {deleteConfirmModal.type === 'single'
                      ? `Are you sure you want to delete this student?`
                      : `Are you sure you want to delete ${deleteConfirmModal.students.length} students?`
                    }
                  </p>
                  <p className="text-sm text-gray-500">
                    This action cannot be undone. Click "Continue" to proceed.
                  </p>
                </div>
              )}

              {deleteConfirmModal.step === 2 && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-8 h-8 text-yellow-500" />
                  </div>
                  <p className="text-gray-700 mb-4 font-semibold">
                    Final Warning!
                  </p>
                  <p className="text-sm text-gray-500">
                    {deleteConfirmModal.type === 'single'
                      ? 'The student and all associated data will be permanently deleted.'
                      : `All ${deleteConfirmModal.students.length} students and their associated data will be permanently deleted.`
                    }
                  </p>
                </div>
              )}

              {deleteConfirmModal.step === 3 && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Trash2 className="w-8 h-8 text-red-500" />
                  </div>
                  <p className="text-gray-700 mb-4 font-bold text-red-600">
                    Last Chance!
                  </p>
                  <p className="text-sm text-gray-500">
                    Click "Delete Forever" to permanently remove the data.
                  </p>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setDeleteConfirmModal({ open: false, type: 'single', email: '', students: [], step: 1 })}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirmation}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors ${deleteConfirmModal.step === 3
                    ? 'bg-red-500 hover:bg-red-600 text-white'
                    : 'bg-[#D67C2C] hover:bg-[#c06a25] text-white'
                    }`}
                >
                  {deleteConfirmModal.step === 3 ? 'Delete Forever' : 'Continue'}
                </button>
              </div>

              <div className="mt-4 text-center">
                <div className="flex justify-center gap-2">
                  {[1, 2, 3].map(step => (
                    <div
                      key={step}
                      className={`w-3 h-3 rounded-full ${step <= deleteConfirmModal.step
                        ? 'bg-red-500'
                        : 'bg-gray-200'
                        }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </Modal>

          {/* Assign Classes Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#D67C2C]" />
                <span>{selectedStudents.length > 0 ? 'Bulk Assign Classes' : 'Assign Classes'}</span>
              </div>
            }
            open={assignModalOpen}
            onCancel={() => setAssignModalOpen(false)}
            footer={null}
          >
            <form
              className="py-4"
              onSubmit={handleAssignClasses}
            >
              <div className="mb-6 p-4 bg-orange-50 rounded-lg">
                <div className="flex items-center gap-4">
                  {selectedStudents.length > 0 ? (
                    <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center text-[#D67C2C] shadow-md">
                      <Users className="w-6 h-6" />
                    </div>
                  ) : selectedStudent?.profile_picture_url ? (
                    <img
                      src={server_url + selectedStudent.profile_picture_url}
                      alt={selectedStudent.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-md"
                    />
                  ) : (
                    <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center text-[#D67C2C] shadow-md">
                      <User2 className="w-6 h-6" />
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-gray-800">
                      {selectedStudents.length > 0
                        ? `${selectedStudents.length} Selected Students`
                        : selectedStudent?.name
                      }
                    </div>
                    <div className="text-sm text-gray-600">
                      Grade: <span className="font-medium text-[#D67C2C]">
                        {selectedStudent?.grade || selectedStudent?.class_grade || "—"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {assigning ? (
                <div className="flex items-center justify-center gap-3 py-8">
                  <div className="w-8 h-8 text-[#D67C2C] animate-spin rounded-full border-4 border-orange-500 border-t-transparent"></div>
                  <span className="text-gray-600">Loading available classes...</span>
                </div>
              ) : availableClasses.length ? (
                <div className="mb-6">
                  {(() => {
                    const assignSectionOptions = Array.from(
                      new Set(availableClasses.map((cls) => cls.section).filter(Boolean))
                    ).sort();
                    return assignSectionOptions.length > 0 ? (
                      <div className="mb-3">
                        <label className="text-sm font-medium text-gray-700 block mb-1">
                          Filter by Section
                        </label>
                        <select
                          value={assignSectionFilter}
                          onChange={(e) => setAssignSectionFilter(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent text-sm"
                        >
                          <option value="">All Sections</option>
                          {assignSectionOptions.map((sec) => (
                            <option key={sec} value={sec}>Section {sec}</option>
                          ))}
                        </select>
                      </div>
                    ) : null;
                  })()}
                  <label className="font-medium block mb-3 text-gray-700">
                    Select Classes to Assign
                  </label>
                  <div className="border-2 border-gray-200 rounded-lg">
                    <select
                      multiple
                      className="w-full px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors min-h-[160px]"
                      value={selectedClasses}
                      onChange={(e) => setSelectedClasses(Array.from(e.target.selectedOptions, (o) => o.value))}
                      required
                    >
                      {availableClasses
                        .filter((cls) => !assignSectionFilter || cls.section === assignSectionFilter)
                        .map((cls) => (
                        <option key={cls.class_id} value={cls.class_id} className="py-2">
                          {cls.name}{cls.section ? ` (Section ${cls.section})` : ''}{cls.grade ? ` • Grade ${cls.grade}` : ''}{cls.teacher_name ? ` — ${cls.teacher_name}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-2 text-sm text-gray-500">
                    Hold Ctrl (or Cmd) to select multiple classes
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 bg-gray-50 rounded-lg mb-6">
                  <div className="text-gray-500 mb-2">No classes found for this grade.</div>
                  <div className="text-sm text-gray-400">Try changing the student's grade first</div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setAssignModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={classNames(
                    "flex-1 rounded-lg py-3 font-semibold transition-colors flex items-center justify-center gap-2",
                    availableClasses.length && selectedClasses.length
                      ? "bg-[#D67C2C] text-white hover:bg-[#c06a25]"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  )}
                  disabled={!availableClasses.length || !selectedClasses.length}
                >
                  <BookOpen className="w-5 h-5" />
                  Assign Classes
                </button>
              </div>
            </form>
          </Modal>

          {/* Promote Student Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <ArrowUpCircle className="w-5 h-5 text-[#D67C2C]" />
                <span>Promote Student</span>
              </div>
            }
            open={promoteModalOpen}
            onCancel={() => setPromoteModalOpen(false)}
            footer={null}
          >
            <form
              className="py-4"
              onSubmit={handlePromote}
            >
              <div className="mb-6 p-4 bg-orange-50 rounded-lg">
                <div className="flex items-center gap-4">
                  {selectedStudent?.profile_picture_url ? (
                    <img
                      src={server_url + selectedStudent.profile_picture_url}
                      alt={selectedStudent.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-md"
                    />
                  ) : (
                    <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center text-[#D67C2C] shadow-md">
                      <User2 className="w-6 h-6" />
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-gray-800">{selectedStudent?.name}</div>
                    <div className="mt-1 px-3 py-1 bg-white text-[#D67C2C] rounded-full text-sm inline-flex items-center shadow-sm">
                      <GraduationCap className="w-4 h-4 mr-1" />
                      Current Grade: {selectedStudent?.grade || selectedStudent?.class_grade || "—"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="font-medium block mb-2 text-gray-700">New Grade</label>
                <select
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors appearance-none bg-white"
                  required
                  value={promoteGrade}
                  onChange={(e) => setPromoteGrade(e.target.value)}
                >
                  <option value="">Select new grade</option>
                  {GRADE_LIST.map((g) => (
                    <option key={g} value={g}>
                      Grade {g}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setPromoteModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-[#D67C2C] text-white rounded-lg py-3 font-semibold hover:bg-[#c06a25] transition-colors flex items-center justify-center gap-2"
                  disabled={!promoteGrade}
                >
                  <ArrowUpCircle className="w-5 h-5" />
                  Promote Student
                </button>
              </div>
            </form>
          </Modal>

          {/* Student Detail Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <User2 className="w-5 h-5 text-[#D67C2C]" />
                <span>Student Details</span>
              </div>
            }
            open={detailModalOpen}
            onCancel={() => { setDetailModalOpen(false); setEditProfileMode(false); }}
            footer={null}
            width={600}
          >
            {/* Header row */}
            <div className="flex items-center gap-4 mb-6 p-4 bg-orange-50 rounded-lg">
              <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center text-[#D67C2C] shadow-md flex-shrink-0">
                {selectedStudent?.profile_picture_url ? (
                  <img src={server_url + selectedStudent.profile_picture_url} alt={selectedStudent.name} className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <User2 className="w-8 h-8" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-gray-800 truncate">{selectedStudent?.name || "—"}</h3>
                <div className="text-sm text-gray-500 truncate">{selectedStudent?.email || "—"}</div>
              </div>
              <button
                onClick={() => editProfileMode ? setEditProfileMode(false) : openEditProfile(selectedStudent)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors flex-shrink-0"
                style={editProfileMode ? {borderColor:'#6b7280', color:'#6b7280'} : {borderColor:'#D67C2C', color:'#D67C2C'}}
              >
                <Edit3 className="w-4 h-4" />
                {editProfileMode ? "Cancel Edit" : "Edit Profile"}
              </button>
            </div>

            {editProfileMode ? (
              /* ── EDIT FORM ── */
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Grade</label>
                    <select value={editProfileData.grade} onChange={e => setEditProfileData(p => ({...p, grade: e.target.value, class_grade: e.target.value}))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent">
                      <option value="">Select grade</option>
                      {GRADE_LIST.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Section</label>
                    <input type="text" value={editProfileData.section} onChange={e => setEditProfileData(p => ({...p, section: e.target.value}))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent" placeholder="e.g. A" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Date of Birth</label>
                    <input type="date" value={editProfileData.date_of_birth} onChange={e => setEditProfileData(p => ({...p, date_of_birth: e.target.value}))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">School Name</label>
                    <input type="text" value={editProfileData.school_name} onChange={e => setEditProfileData(p => ({...p, school_name: e.target.value}))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Number of Siblings</label>
                    <input type="number" min="0" value={editProfileData.number_of_siblings} onChange={e => setEditProfileData(p => ({...p, number_of_siblings: e.target.value}))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Siblings Ages <span className="text-gray-400 font-normal">(comma-separated)</span></label>
                    <input type="text" value={editProfileData.siblings_age} onChange={e => setEditProfileData(p => ({...p, siblings_age: e.target.value}))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent" placeholder="e.g. 5, 8, 12" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Mother's Name</label>
                    <input type="text" value={editProfileData.mother_name} onChange={e => setEditProfileData(p => ({...p, mother_name: e.target.value}))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Father's Name</label>
                    <input type="text" value={editProfileData.father_name} onChange={e => setEditProfileData(p => ({...p, father_name: e.target.value}))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                    <input type="text" value={editProfileData.phone} onChange={e => setEditProfileData(p => ({...p, phone: e.target.value}))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                    <input type="text" value={editProfileData.address} onChange={e => setEditProfileData(p => ({...p, address: e.target.value}))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                  <button onClick={() => setEditProfileMode(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm">
                    Cancel
                  </button>
                  <button onClick={handleSaveProfile} disabled={savingProfile}
                    className="px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] disabled:opacity-50 text-sm flex items-center gap-2">
                    {savingProfile ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    {savingProfile ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            ) : (
              /* ── VIEW MODE ── */
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Grade</div>
                    <div className="font-medium text-gray-800">{selectedStudent?.grade || selectedStudent?.class_grade || "—"}</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">School</div>
                    <div className="font-medium text-gray-800">{selectedStudent?.school_name || "—"}</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Date of Birth</div>
                    <div className="font-medium text-gray-800">{selectedStudent?.date_of_birth ? String(selectedStudent.date_of_birth).slice(0,10) : "—"}</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Number of Siblings</div>
                    <div className="font-medium text-gray-800">{selectedStudent?.number_of_siblings ?? "—"}</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Siblings Ages</div>
                    <div className="font-medium text-gray-800">
                      {Array.isArray(selectedStudent?.siblings_age) && selectedStudent.siblings_age.length > 0
                        ? selectedStudent.siblings_age.join(", ")
                        : "—"}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Section</div>
                    <div className="font-medium text-gray-800">{selectedStudent?.section || "—"}</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Mother's Name</div>
                    <div className="font-medium text-gray-800">{selectedStudent?.mother_name || "—"}</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Father's Name</div>
                    <div className="font-medium text-gray-800">{selectedStudent?.father_name || "—"}</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Phone</div>
                    <div className="font-medium text-gray-800">{selectedStudent?.phone || "—"}</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Last Login</div>
                    <div className="font-medium text-gray-800">{selectedStudent?.last_login ? formatDate(selectedStudent.last_login) : "Never"}</div>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="text-xs text-gray-500 mb-2">Enrolled Classes</div>
                  {Array.isArray(selectedStudent?.enrolled_classes) && selectedStudent.enrolled_classes.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {selectedStudent.enrolled_classes.map((c) => (
                        <span
                          key={c.class_id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm"
                        >
                          <BookOpen className="w-3.5 h-3.5" />
                          {c.name ? `${c.name} — ` : ''}Grade {c.grade}{c.section ? ` - ${c.section}` : ''}
                          {c.teacher_name ? ` (${c.teacher_name})` : ''}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">Not assigned to any class yet</div>
                  )}
                </div>

                <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                  <button onClick={() => { setDetailModalOpen(false); openAssignClasses(selectedStudent); }}
                    className="px-3 py-1.5 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] text-sm flex items-center gap-1.5">
                    <BookOpen className="w-4 h-4" /> Assign Classes
                  </button>
                  <button onClick={() => { setDetailModalOpen(false); openPromote(selectedStudent); }}
                    className="px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm flex items-center gap-1.5">
                    <ArrowUpCircle className="w-4 h-4" /> Promote
                  </button>
                  <button onClick={() => { setDetailModalOpen(false); openDeleteConfirmation('single', selectedStudent.email); }}
                    className="px-3 py-1.5 bg-red-50 text-red-500 rounded-lg hover:bg-red-100 text-sm flex items-center gap-1.5">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                  <button onClick={() => { setDetailModalOpen(false); setEditProfileMode(false); }}
                    className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm">
                    Close
                  </button>
                </div>
              </>
            )}
          </Modal>

          {/* Skipped Records Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                <span>Skipped Records</span>
              </div>
            }
            open={showSkippedModal}
            onCancel={() => setShowSkippedModal(false)}
            footer={
              <button
                onClick={() => setShowSkippedModal(false)}
                className="px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors"
              >
                Close
              </button>
            }
            width={600}
          >
            {(uploadResult?.skipped?.length > 0 || uploadResult?.parent_link_warnings?.length > 0) ? (
              <div>
                <p className="text-gray-600 mb-4">
                  Review the records that could not be imported or linked:
                </p>
                <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Email</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Name</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...(uploadResult.skipped || []), ...(uploadResult.parent_link_warnings || []).map((item) => ({
                        email: item.parent_email, name: item.student, reason: `Parent link: ${item.reason}`
                      }))].map((item, idx) => (
                        <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-600">{item.email}</td>
                          <td className="px-4 py-2 text-gray-600">{item.name || "-"}</td>
                          <td className="px-4 py-2">
                            <span className="text-red-600 text-xs bg-red-50 px-2 py-1 rounded">
                              {item.reason}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                  <strong>Tip:</strong> Fix these issues in your CSV file and re-upload the corrected records.
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No skipped records to display.</p>
            )}
          </Modal>

          {/* Bulk Profile Update Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                <span>Bulk Update Student Profiles</span>
              </div>
            }
            open={profileUploadModalOpen}
            onCancel={() => setProfileUploadModalOpen(false)}
            footer={null}
            width={700}
          >
            <form className="py-4" onSubmit={handleBulkProfileUpload}>
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <div className="text-blue-700 font-medium mb-2">How it works:</div>
                <ul className="text-sm text-blue-600 space-y-1 list-disc pl-5">
                  <li>Upload a CSV with student emails and profile data</li>
                  <li>Students are matched by their email address</li>
                  <li>Only existing students will be updated</li>
                  <li>Empty fields in CSV will be skipped (not overwritten)</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="text-gray-700 font-medium mb-2">Required CSV Format:</div>
                <div className="font-mono text-xs bg-white p-2 rounded border border-gray-200 overflow-x-auto">
                  email,school_name,roll_no,section,date_of_birth,mother_name,father_name,...
                </div>
                <div className="text-sm text-gray-600 mt-2">
                  <strong>Required column:</strong> email (to identify students)
                </div>
              </div>

              {/* Supported Fields Info */}
              {profileTemplateInfo && (
                <div className="mb-6">
                  <details className="bg-gray-50 rounded-lg">
                    <summary className="p-4 cursor-pointer text-gray-700 font-medium hover:bg-gray-100 rounded-lg">
                      View All Supported Fields ({Object.values(profileTemplateInfo.field_categories || {}).flat().length} fields)
                    </summary>
                    <div className="p-4 pt-0 max-h-60 overflow-y-auto">
                      {Object.entries(profileTemplateInfo.field_categories || {}).map(([category, fields]) => (
                        <div key={category} className="mb-3">
                          <div className="text-sm font-medium text-gray-600 capitalize mb-1">
                            {category.replace(/_/g, ' ')}:
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {fields.map(field => (
                              <span key={field} className="text-xs bg-white px-2 py-1 rounded border border-gray-200">
                                {field}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              )}

              {/* File Upload */}
              <div className="mb-6">
                <label className="font-medium block mb-2 text-gray-700">Upload CSV File *</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleProfileCSVChange}
                  className="w-full border-2 border-dashed border-gray-200 p-3 rounded-lg text-gray-700 focus:outline-none focus:border-blue-500 transition-colors cursor-pointer"
                  required
                />
              </div>

              <div className="flex items-center gap-4 mb-6">
                <button
                  type="button"
                  onClick={downloadProfileCSVTemplate}
                  className="inline-flex gap-2 text-blue-500 hover:text-blue-600 transition-colors items-center text-sm font-medium"
                >
                  <FileDown className="w-4 h-4" /> Download Template CSV
                </button>
              </div>

              {/* Validation Summary */}
              {profileValidation && (
                <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-gray-50">
                  <div className="font-medium text-gray-700 mb-3">Validation Summary</div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span>Total Rows: <strong>{profileValidation.total_rows || 0}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span>Valid: <strong>{profileValidation.valid_count || 0}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <span>Not Found: <strong>{profileValidation.invalid_count || 0}</strong></span>
                    </div>
                  </div>
                  {profileValidation.valid_count > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <span className="text-green-600 font-medium">
                        ✓ {profileValidation.valid_count} student profiles will be updated
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Validating indicator */}
              {validatingProfile && (
                <div className="mb-4 flex items-center gap-2 text-gray-600">
                  <div className="w-4 h-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
                  <span>Validating CSV...</span>
                </div>
              )}

              {/* Preview */}
              {profileCsvPreview.length > 0 && (
                <div className="mb-6">
                  <div className="font-medium mb-2 text-gray-700">Preview (First 20 rows):</div>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm overflow-x-auto max-h-60 border border-gray-200">
                    {/* Header */}
                    <div className="font-mono font-semibold text-gray-700 py-2 border-b-2 border-gray-300 flex gap-4">
                      <span className="w-10">Row</span>
                      <span className="flex-1">Email</span>
                      <span className="w-20">Name</span>
                      <span className="w-16">Fields</span>
                      <span className="w-32">Status</span>
                    </div>
                    {/* Data rows */}
                    {profileCsvPreview.map((row, i) => {
                      const statusColors = {
                        valid: "bg-green-50 border-l-4 border-green-500",
                        not_found: "bg-red-50 border-l-4 border-red-500",
                        pending: "bg-gray-50 border-l-4 border-gray-300"
                      };
                      const statusLabels = {
                        valid: <span className="text-green-600">✓ Found</span>,
                        not_found: <span className="text-red-600">✗ Not Found</span>,
                        pending: <span className="text-gray-500">Pending</span>
                      };

                      return (
                        <div
                          key={i}
                          className={`font-mono py-2 border-b last:border-0 flex gap-4 items-center ${statusColors[row.status] || ""}`}
                        >
                          <span className="w-10 text-gray-500">{row.row}</span>
                          <span className="flex-1 truncate">{row.email || "-"}</span>
                          <span className="w-20 truncate">{row.name || "-"}</span>
                          <span className="w-16 text-center">{row.fields_to_update || row.fields_count || 0}</span>
                          <span className="w-32 text-xs">
                            {statusLabels[row.status] || row.status}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setProfileUploadModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-500 text-white rounded-lg py-3 font-semibold hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                  disabled={!profileCsvFile || (profileValidation && profileValidation.valid_count === 0)}
                >
                  <FileText className="w-5 h-5" />
                  Update Profiles
                </button>
              </div>
            </form>
          </Modal>

          {/* Profile Update Skipped Records Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                <span>Skipped Profile Updates</span>
              </div>
            }
            open={showProfileSkippedModal}
            onCancel={() => setShowProfileSkippedModal(false)}
            footer={
              <button
                onClick={() => setShowProfileSkippedModal(false)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Close
              </button>
            }
            width={600}
          >
            {profileUploadResult?.skipped && profileUploadResult.skipped.length > 0 ? (
              <div>
                <p className="text-gray-600 mb-4">
                  The following {profileUploadResult.skipped.length} record(s) were skipped during profile update:
                </p>
                <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Email</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profileUploadResult.skipped.map((item, idx) => (
                        <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-600">{item.email}</td>
                          <td className="px-4 py-2">
                            <span className="text-red-600 text-xs bg-red-50 px-2 py-1 rounded">
                              {item.reason}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                  <strong>Tip:</strong> Make sure the email addresses match existing students in your school. Students must be created first before their profiles can be updated.
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No skipped records to display.</p>
            )}
          </Modal>
        </div>
      </div>
    </>
  );
}
